import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Tag,
  Space,
  message,
  Modal,
  Form,
  Input,
  Card,
  Typography,
  Tooltip,
  Popconfirm,
  Upload,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  DeleteOutlined,
  MessageOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { customizationAPI } from '../api';
import { BRAND_PRIMARY } from '../utils/brandColors';
import { normalizeMoney } from '../utils/money';
import CustomizationDialog from './CustomizationDialog';
import { compressImage, isImageFile, formatFileSize } from '../utils/imageCompression';

const { Title } = Typography;
const { TextArea } = Input;

const MyCustomizations = () => {
  const [customizations, setCustomizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedCustomization, setSelectedCustomization] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCustomizations();
  }, []);

  const fetchCustomizations = async () => {
    setLoading(true);
    try {
      const response = await customizationAPI.getAll();
      setCustomizations(response.data);
    } catch (error) {
      message.error('Failed to fetch customizations');
      
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomization = async (values) => {
    setCreateLoading(true);
    try {
      // 第一步：创建定制需求（只包含customization_name）
      const response = await customizationAPI.create({ customization_name: values.customization_name });
      
      // 第二步：处理附件（压缩图片）
      const processedFiles = [];
      for (const file of fileList) {
        if (isImageFile(file.originFileObj)) {
          // 图片文件：检查大小并压缩
          if (file.originFileObj.size > 1024 * 1024) { // 超过1MB
            message.loading(`Compressing image ${file.name}...`, 0);
            try {
              const compressedFile = await compressImage(file.originFileObj, 700); // 压缩到700KB
              processedFiles.push(compressedFile);
              message.destroy();
              message.success(`Image ${file.name} compressed (${formatFileSize(file.originFileObj.size)} → ${formatFileSize(compressedFile.size)})`);
            } catch (error) {
              message.destroy();
              message.warning(`Image ${file.name} compression failed, using original file`);
              processedFiles.push(file.originFileObj);
            }
          } else {
            processedFiles.push(file.originFileObj);
          }
        } else {
          // 非图片文件：直接添加
          processedFiles.push(file.originFileObj);
        }
      }
      
      // 第三步：添加第一条消息（包含文本和处理后的附件）
      const formData = new FormData();
      formData.append('content', values.first_comment);
      
      // 添加处理后的附件
      processedFiles.forEach((file) => {
        formData.append('files', file);
      });
      
      await customizationAPI.addComment(response.data.id, formData);
      
      message.success('Customization created successfully');
      setCreateModalVisible(false);
      form.resetFields();
      setFileList([]);
      fetchCustomizations();
      // 创建成功后立即打开对话框
      setSelectedCustomization(response.data);
      setDialogVisible(true);
    } catch (error) {
      message.error('Failed to create customization');
      
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteCustomization = async (customizationId) => {
    try {
      await customizationAPI.delete(customizationId);
      message.success('Customization deleted successfully');
      fetchCustomizations();
    } catch (error) {
      message.error('Failed to delete customization');
      
    }
  };

  const handleViewCustomization = (customization) => {
    setSelectedCustomization(customization);
    setDialogVisible(true);
  };

  const handleDialogClose = () => {
    setDialogVisible(false);
    setSelectedCustomization(null);
    fetchCustomizations(); // 刷新列表以更新状态
  };

  const columns = [
    {
      title: 'Customization Name',
      dataIndex: 'customization_name',
      key: 'customization_name',
      width: '25%',
      ellipsis: true,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: (status) => (
        <Tag 
          color={status === 'approved' ? 'green' : BRAND_PRIMARY}
          icon={status === 'approved' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
        >
          {status === 'approved' ? 'Approved' : 'Unapproved'}
        </Tag>
      ),
    },
    {
      title: 'Price per Package', 
      dataIndex: 'price_prepacket',
      key: 'price_prepacket',
      render: (price) => (price !== null && price !== undefined) ? normalizeMoney(price) : 'Not Set',
      width: '15%',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '15%',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Last Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: '15%',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Action',
      key: 'action',
      width: '10%',
      render: (_, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Delete customization">
            <Popconfirm
              title="Are you sure to delete this customization?"
              description="This action cannot be undone and will delete all conversations and attachments."
              onConfirm={(e) => {
                e?.stopPropagation();
                handleDeleteCustomization(record.id);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>My Customizations</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            Apply New Customization
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={customizations}
          loading={loading}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => handleViewCustomization(record),
            style: { cursor: 'pointer' }
          })}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} customizations`,
          }}
          size="middle"
        />
      </Card>

      {/* Create Customization Modal */}
      <Modal
        title="Apply for New Customization"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
          setFileList([]);
        }}
        onOk={() => form.submit()}
        confirmLoading={createLoading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateCustomization}
        >
          <Form.Item
            name="customization_name"
            label="Customization Name"
            rules={[
              { required: true, message: 'Please enter customization name' },
              { min: 2, message: 'Customization name must be at least 2 characters' },
              { max: 50, message: 'Customization name cannot exceed 50 characters' },
            ]}
          >
            <Input placeholder="Enter your customization name" />
          </Form.Item>
          <Form.Item
            name="first_comment"
            label="Description"
            rules={[
              { required: true, message: 'Please enter description' },
              { min: 10, message: 'Message must be at least 10 characters' },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Describe your customization requirements, like logo, packaging specifications, etc."
            />
          </Form.Item>
          <Form.Item
            label="Attachments"
          >
            <Upload
              beforeUpload={(file) => {
                const allowedTypes = [
                  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
                  'application/pdf',
                  'application/msword',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  'application/vnd.ms-powerpoint',
                  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                  'application/vnd.ms-excel',
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                ];

                if (!allowedTypes.includes(file.type)) {
                  message.error('File type not supported. Please upload images, PDF, DOC, DOCX, PPT, PPTX, XLS, or XLSX files.');
                  return false;
                }

                // 图片文件限制1MB（会自动压缩），其他文件限制3MB
                const isImage = file.type.startsWith('image/');
                const maxSize = isImage ? 1024 * 1024 : 3 * 1024 * 1024; // 1MB for images, 3MB for others
                const sizeLabel = isImage ? '1MB' : '3MB';
                
                if (file.size > maxSize) {
                  if (isImage) {
                    message.warning(`Image ${file.name} exceeds 1MB, will be automatically compressed before upload`);
                    // 图片文件允许添加，因为会自动压缩
                  } else {
                    message.error(`File size cannot exceed ${sizeLabel}`);
                    return Upload.LIST_IGNORE; // 阻止文件添加到列表
                  }
                }

                return false; // Don't auto upload
              }}
              fileList={fileList}
              onChange={({ fileList: newFileList }) => {
                setFileList(newFileList);
              }}
              multiple
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
            >
              <Button icon={<PaperClipOutlined />}>
                Select Files
              </Button>
            </Upload>
            <br />
            <p>Supported file types: images and documents (PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX)<br />File size cannot exceed 3MB for documents.</p>
          </Form.Item>
        </Form>
      </Modal>

      {/* Customization Dialog */}
      {dialogVisible && selectedCustomization && (
        <CustomizationDialog
          customization={selectedCustomization}
          visible={dialogVisible}
          onClose={handleDialogClose}
        />
      )}
    </div>
  );
};

export default MyCustomizations;
